import { useDeferredValue, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { authApi } from '@/services/auth.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, EmptyState, Spinner } from '@/components/ui';
import { LogoMark } from '@/components/logo-mark';
import { avatarColorFor } from '@/components/avatar-color';

function churchInitial(name: string): string {
  return name.trim().charAt(0).toUpperCase() || '?';
}

/**
 * Tenant selection.
 *
 * Only reached by users who belong to more than one church — anyone with a
 * single membership is placed in it at login. Making the choice explicit means
 * no request silently executes against the wrong congregation's data.
 *
 * The list is queried rather than read from the auth context alone. The token
 * lives in sessionStorage and so survives a reload, but the login response that
 * carried the memberships does not: after F5 the context holds an empty array,
 * and this screen used to render a spinner that nothing would ever resolve.
 * Login remains a single round trip, because the memberships it already
 * returned seed the query.
 */
export function SelectChurchPage() {
  const { memberships: fromLogin, selectTenant } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  const [selecting, setSelecting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);

  const query = useQuery({
    queryKey: ['auth', 'memberships'],
    queryFn: () => authApi.listMemberships(),
    // Present only on the login path. Supplying it marks the data fresh, so
    // signing in does not immediately refetch what it was just handed.
    initialData: fromLogin.length > 0 ? fromLogin : undefined,
  });

  async function choose(tenantId: string): Promise<void> {
    setSelecting(tenantId);
    setError(null);
    try {
      await selectTenant(tenantId);
      void navigate('/people', { replace: true });
    } catch {
      setError(t('errors.INTERNAL_ERROR'));
      setSelecting(null);
    }
  }

  const memberships = query.data ?? [];
  const normalizedSearch = deferredSearch.trim().toLowerCase();
  const visibleMemberships = normalizedSearch
    ? memberships.filter(
        (membership) =>
          membership.tenantName.toLowerCase().includes(normalizedSearch) ||
          membership.tenantSlug.toLowerCase().includes(normalizedSearch),
      )
    : memberships;

  return (
    <main className="auth-page">
      <div className="auth-card church-select-card">
        <header className="auth-header">
          <LogoMark size={40} />
          <h1 className="auth-title">{t('auth.selectTenant')}</h1>
          <p className="auth-subtitle">{t('auth.selectTenantHint')}</p>
        </header>

        {error && <Alert tone="danger">{error}</Alert>}

        {query.isError && (
          <Alert tone="danger">
            {query.error instanceof ApiError
              ? t(`errors.${query.error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
              : t('errors.INTERNAL_ERROR')}
          </Alert>
        )}

        {query.isPending && <Spinner label={t('common.states.loading')} />}

        {query.isError && (
          <Button onClick={() => void query.refetch()}>{t('common.actions.retry')}</Button>
        )}

        {/*
          An account with no memberships is a real state, not a slow one: a user
          invited to the platform but not yet added to a church. Saying so beats
          a spinner that never stops, which is what this screen did for every
          reload.
        */}
        {query.isSuccess && memberships.length === 0 && (
          <EmptyState title={t('auth.noChurches')} hint={t('auth.noChurchesHint')} />
        )}

        {memberships.length > 0 && (
          <>
            <label className="church-search">
              <svg
                className="church-search-icon"
                viewBox="0 0 20 20"
                fill="none"
                aria-hidden="true"
              >
                <circle cx="9" cy="9" r="6.5" stroke="currentColor" strokeWidth="1.6" />
                <path
                  d="M14 14L18 18"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              </svg>
              <span className="sr-only">{t('auth.searchChurches')}</span>
              <input
                type="search"
                className="church-search-input"
                placeholder={t('auth.searchChurches')}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                autoComplete="off"
              />
            </label>

            {visibleMemberships.length === 0 ? (
              <EmptyState title={t('auth.noSearchResults')} />
            ) : (
              <ul className="church-list">
                {visibleMemberships.map((membership) => {
                  const { background, foreground } = avatarColorFor(membership.tenantName);
                  return (
                    <li key={membership.tenantId}>
                      <Button
                        onClick={() => void choose(membership.tenantId)}
                        loading={selecting === membership.tenantId}
                        disabled={selecting !== null}
                        className="church-option"
                      >
                        <span
                          className="church-avatar"
                          style={{ background, color: foreground }}
                          aria-hidden="true"
                        >
                          {churchInitial(membership.tenantName)}
                        </span>
                        <span className="church-option-body">
                          <span className="church-name">{membership.tenantName}</span>
                          <span className="church-slug">{membership.tenantSlug}</span>
                        </span>
                        <span className="church-roles">
                          {membership.roles.map((role) => (
                            <span key={role} className="church-role-chip">
                              {role}
                            </span>
                          ))}
                        </span>
                        <svg
                          className="church-option-arrow"
                          viewBox="0 0 20 20"
                          fill="none"
                          aria-hidden="true"
                        >
                          <path
                            d="M7.5 4.5L13 10L7.5 15.5"
                            stroke="currentColor"
                            strokeWidth="1.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </div>
    </main>
  );
}
